'use client';

import React from 'react';
import {
  Wallet,
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowRight,
  Clock,
  CheckCircle2,
  ExternalLink,
  AlertCircle,
  TrendingUp,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  BarChart,
  Bar,
  Legend,
  Cell,
} from 'recharts';
import { trpc } from '@/lib/api/trpc';

function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
  isLoading,
  className,
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ComponentType<{ className?: string }>;
  isLoading?: boolean;
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="text-muted-foreground h-4 w-4" />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-7 w-32" />
        ) : (
          <>
            <div className="text-2xl font-bold">{value}</div>
            {subtitle && <p className="text-muted-foreground text-xs">{subtitle}</p>}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function shortenHash(hash: string): string {
  if (!hash || hash.length < 16) return hash || '—';
  return `${hash.slice(0, 6)}...${hash.slice(-4)}`;
}

function fmt(n: number): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/* ── Funds Flow Diagram ───────────────────────────────────────────────── */

function FundsFlow({
  customerPaid,
  poolReceived,
  netToMerchants,
  tagadaKeeps,
  pendingNet,
  pendingPool,
  poolBalance,
  feeBalance,
}: {
  customerPaid: number;
  poolReceived: number;
  netToMerchants: number;
  tagadaKeeps: number;
  pendingNet: number;
  pendingPool: number;
  poolBalance: number | null;
  feeBalance: number | null;
}) {
  const rampexFee = customerPaid - poolReceived;

  return (
    <div className="flex flex-col gap-4">
      {/* Row 1: Customer → Rampex → Pool */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 rounded-lg border bg-blue-50 p-3 dark:bg-blue-950/30 text-center">
          <p className="text-xs text-muted-foreground">Customer Paid</p>
          <p className="text-lg font-bold text-blue-700 dark:text-blue-400">${fmt(customerPaid)}</p>
        </div>
        <div className="flex flex-col items-center text-xs text-muted-foreground">
          <span className="text-red-500 font-medium">-${fmt(rampexFee)}</span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <span className="text-red-500">Rampex+Provider</span>
          <span className="text-red-500 font-mono text-[10px]">({((rampexFee / customerPaid) * 100).toFixed(1)}%)</span>
        </div>
        <div className="flex-1 rounded-lg border bg-emerald-50 p-3 dark:bg-emerald-950/30 text-center">
          <p className="text-xs text-muted-foreground">Pool Wallet Received</p>
          <p className="text-lg font-bold text-emerald-700 dark:text-emerald-400">${fmt(poolReceived)}</p>
          {poolBalance != null && (
            <p className="text-[10px] text-muted-foreground mt-1">
              Live balance: <span className="font-semibold">${fmt(poolBalance)}</span>
            </p>
          )}
        </div>
      </div>

      {/* Row 2: Pool splits → Merchant + Tagada Fee */}
      <div className="flex flex-wrap items-start justify-center gap-4 sm:gap-6">
        <div className="flex flex-col items-center gap-1">
          <ArrowRight className="h-4 w-4 text-emerald-600 rotate-90" />
          <div className="rounded-lg border bg-green-50 p-3 dark:bg-green-950/30 text-center min-w-[160px]">
            <p className="text-xs text-muted-foreground">Net to Merchants</p>
            <p className="text-lg font-bold text-green-700 dark:text-green-400">${fmt(netToMerchants)}</p>
            <p className="text-[10px] text-muted-foreground">paid out</p>
          </div>
        </div>
        <div className="flex flex-col items-center gap-1">
          <ArrowRight className="h-4 w-4 text-purple-600 rotate-90" />
          <div className="rounded-lg border bg-purple-50 p-3 dark:bg-purple-950/30 text-center min-w-[160px]">
            <p className="text-xs text-muted-foreground">Tagada Fees</p>
            <p className="text-lg font-bold text-purple-700 dark:text-purple-400">${fmt(tagadaKeeps)}</p>
            {feeBalance != null && (
              <p className="text-[10px] text-muted-foreground mt-1">
                Fee wallet: <span className="font-semibold">${fmt(feeBalance)}</span>
              </p>
            )}
          </div>
        </div>
        {pendingNet > 0 && (
          <div className="flex flex-col items-center gap-1">
            <Clock className="h-4 w-4 text-amber-600" />
            <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50/50 p-3 dark:bg-amber-950/20 text-center min-w-[160px]">
              <p className="text-xs text-muted-foreground">Pending Payout</p>
              <p className="text-lg font-bold text-amber-700 dark:text-amber-400">${fmt(pendingNet)}</p>
              <p className="text-[10px] text-muted-foreground">pool: ${fmt(pendingPool)}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Scatter Chart ────────────────────────────────────────────────────── */

function FeeScatterChart({ data }: { data: { date: string; amount: number; rampexPct: number; tagadaPct: number }[] }) {
  const sorted = [...data].sort((a, b) => a.date.localeCompare(b.date));
  const indexed = sorted.map((d, i) => ({ ...d, idx: i }));

  const rampexColor = '#ef4444';
  const tagadaColor = '#8b5cf6';

  return (
    <ResponsiveContainer width="100%" height={340}>
      <ScatterChart margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis
          dataKey="idx"
          type="number"
          domain={[0, indexed.length - 1]}
          tickFormatter={(i: number) => indexed[i]?.date?.slice(5) || ''}
          tick={{ fontSize: 10 }}
          label={{ value: 'Transaction (chronological)', position: 'insideBottom', offset: -18, style: { fontSize: 11 } }}
        />
        <YAxis
          domain={[0, 10]}
          tickFormatter={(v: number) => `${v}%`}
          tick={{ fontSize: 10 }}
          label={{ value: 'Fee %', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
        />
        <ReferenceLine y={7.5} stroke="#f59e0b" strokeDasharray="6 4" label={{ value: '7.5% merchant fee', position: 'right', style: { fontSize: 10, fill: '#f59e0b' } }} />
        <Tooltip
          content={({ payload }) => {
            if (!payload?.length) return null;
            const d = payload[0]?.payload;
            if (!d) return null;
            return (
              <div className="rounded-lg border bg-background p-2 text-xs shadow-md">
                <p className="font-medium">{d.date} — ${fmt(d.amount)}</p>
                <p style={{ color: rampexColor }}>Rampex: {d.rampexPct}%</p>
                <p style={{ color: tagadaColor }}>Tagada: {d.tagadaPct}%</p>
              </div>
            );
          }}
        />
        <Scatter name="Rampex+Provider %" data={indexed} dataKey="rampexPct" fill={rampexColor} fillOpacity={0.6} r={3} />
        <Scatter name="Tagada %" data={indexed} dataKey="tagadaPct" fill={tagadaColor} fillOpacity={0.6} r={3} />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

/* ── Daily Stacked Bar Chart ──────────────────────────────────────────── */

function DailyFeeBarChart({ data }: { data: { date: string; rampexPct: number; tagadaPct: number }[] }) {
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v: string) => v.slice(5)} />
        <YAxis tickFormatter={(v: number) => `${v}%`} tick={{ fontSize: 10 }} />
        <Tooltip
          formatter={(value: number, name: string) => [`${value}%`, name]}
          contentStyle={{ fontSize: 12 }}
        />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="rampexPct" name="Rampex+Provider" fill="#ef4444" stackId="a" radius={[0, 0, 0, 0]} />
        <Bar dataKey="tagadaPct" name="Tagada Margin" fill="#8b5cf6" stackId="a" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/* ── Main Page ────────────────────────────────────────────────────────── */

export default function AdminCryptoPage() {
  const { data, isLoading, error } = trpc.admin.cryptoOverview.useQuery(undefined, {
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="py-8 text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-destructive" />
          <p className="text-destructive mt-2 font-medium">Failed to load crypto data</p>
          <p className="text-muted-foreground mt-1 text-sm">{error.message}</p>
        </CardContent>
      </Card>
    );
  }

  const s = data?.summary;
  const w = data?.wallets;

  return (
    <div className="space-y-6">
      {/* Live Wallet Balances */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="border-emerald-200 bg-emerald-50/30 dark:border-emerald-900 dark:bg-emerald-950/20">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pool Wallet (live on-chain)</CardTitle>
            <Wallet className="text-emerald-600 h-4 w-4" />
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-7 w-32" /> : w?.pool ? (
              <>
                <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">
                  ${fmt(w.pool.total)} <span className="text-sm font-normal text-muted-foreground">USDC</span>
                </div>
                <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                  <span>Native: ${fmt(w.pool.usdc)}</span>
                  {w.pool.usdcBridged > 0 && <span>Bridged: ${fmt(w.pool.usdcBridged)}</span>}
                </div>
                <a
                  href={`https://polygonscan.com/address/${w.pool.address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-flex items-center gap-1 font-mono text-xs text-blue-500 hover:underline"
                >
                  {w.pool.address.slice(0, 8)}...{w.pool.address.slice(-6)}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </>
            ) : (
              <p className="text-muted-foreground text-sm">Not configured</p>
            )}
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50/30 dark:border-purple-900 dark:bg-purple-950/20">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tagada Fee Wallet (live on-chain)</CardTitle>
            <TrendingUp className="text-purple-600 h-4 w-4" />
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-7 w-32" /> : w?.fee ? (
              <>
                <div className="text-2xl font-bold text-purple-700 dark:text-purple-400">
                  ${fmt(w.fee.total)} <span className="text-sm font-normal text-muted-foreground">USDC</span>
                </div>
                <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                  <span>Native: ${fmt(w.fee.usdc)}</span>
                  {w.fee.usdcBridged > 0 && <span>Bridged: ${fmt(w.fee.usdcBridged)}</span>}
                </div>
                <a
                  href={`https://polygonscan.com/address/${w.fee.address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-flex items-center gap-1 font-mono text-xs text-blue-500 hover:underline"
                >
                  {w.fee.address.slice(0, 8)}...{w.fee.address.slice(-6)}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </>
            ) : (
              <p className="text-muted-foreground text-sm">Not configured</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Funds Flow Diagram */}
      {s && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Funds Flow</CardTitle>
            <CardDescription>Visual breakdown of money flow from customer payment to merchant payout</CardDescription>
          </CardHeader>
          <CardContent>
            <FundsFlow
              customerPaid={s.totalCustomerPaid}
              poolReceived={s.totalPoolReceived}
              netToMerchants={s.totalNetToMerchants}
              tagadaKeeps={s.tagadaKeepsTotal}
              pendingNet={s.totalPendingNet}
              pendingPool={s.totalPendingPool}
              poolBalance={w?.pool?.total ?? null}
              feeBalance={w?.fee?.total ?? null}
            />
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Customer Volume"
          value={s ? `$${fmt(s.totalCustomerPaid)}` : '—'}
          subtitle={s ? `${s.totalTransactions} transactions` : undefined}
          icon={Wallet}
          isLoading={isLoading}
        />
        <MetricCard
          title="Pool Received (on-chain)"
          value={s ? `$${fmt(s.totalPoolReceived)}` : '—'}
          subtitle={s ? `${s.verifiedCount} verified • Rampex+provider fee: ${s.rampexFeePct}%` : undefined}
          icon={ArrowDownToLine}
          isLoading={isLoading}
        />
        <MetricCard
          title="Net Sent to Merchants"
          value={s ? `$${fmt(s.totalNetToMerchants)}` : '—'}
          subtitle={s ? `${s.paidOutCount} txns paid out` : undefined}
          icon={ArrowUpFromLine}
          isLoading={isLoading}
        />
        <MetricCard
          title="Pending Payout"
          value={s ? `$${fmt(s.totalPendingNet)}` : '—'}
          subtitle={s ? `${s.pendingCount} txns • pool: $${fmt(s.totalPendingPool)}` : undefined}
          icon={Clock}
          isLoading={isLoading}
        />
      </div>

      {/* Tagada Profit Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Tagada Earned (paid batches)</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-7 w-32" />
            ) : (
              <>
                <div className="text-2xl font-bold text-emerald-600">
                  ${fmt(s?.tagadaKeepsPaid ?? 0)}
                </div>
                <p className="text-muted-foreground text-xs">
                  Pool received for paid txns − net sent to merchants
                </p>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Tagada Pending Margin</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-7 w-32" />
            ) : (
              <>
                <div className="text-2xl font-bold text-amber-600">
                  ${fmt(s?.tagadaKeepsPending ?? 0)}
                </div>
                <p className="text-muted-foreground text-xs">
                  Projected margin on pending txns (pool − net)
                </p>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Tagada Profit</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-7 w-32" />
            ) : (
              <>
                <div className="text-2xl font-bold text-emerald-600">
                  ${fmt(s?.tagadaKeepsTotal ?? 0)}
                </div>
                <p className="text-muted-foreground text-xs">
                  Earned + pending margin
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Fee Charts Side by Side */}
      {data?.scatterData && data.scatterData.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Scatter Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Per-Transaction Fee % (scatter)</CardTitle>
              <CardDescription>Zoomed 0–10% — dashed line = 7.5% merchant fee</CardDescription>
            </CardHeader>
            <CardContent>
              <FeeScatterChart data={data.scatterData} />
            </CardContent>
          </Card>

          {/* Daily Stacked Bar */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Daily Fee % (volume-weighted)</CardTitle>
              <CardDescription>Rampex+Provider vs Tagada margin stacked by day</CardDescription>
            </CardHeader>
            <CardContent>
              <DailyFeeBarChart data={data.dailyBreakdown} />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Daily Breakdown Table */}
      {data?.dailyBreakdown && data.dailyBreakdown.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Daily Breakdown (volume-weighted)</CardTitle>
            <CardDescription>Per-day crypto transaction summary with volume-weighted fee percentages</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Txns</TableHead>
                  <TableHead className="text-right">Volume</TableHead>
                  <TableHead className="text-right">Pool Recv</TableHead>
                  <TableHead className="text-right">Rampex Fee</TableHead>
                  <TableHead className="text-right">Rampex %</TableHead>
                  <TableHead className="text-right">Tagada Keeps</TableHead>
                  <TableHead className="text-right">Tagada %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.dailyBreakdown.map(d => (
                  <TableRow key={d.date}>
                    <TableCell className="font-mono text-sm">{d.date}</TableCell>
                    <TableCell className="text-right tabular-nums">{d.txns}</TableCell>
                    <TableCell className="text-right tabular-nums">${fmt(d.volume)}</TableCell>
                    <TableCell className="text-right tabular-nums">${fmt(d.poolRecv)}</TableCell>
                    <TableCell className="text-right tabular-nums text-red-600">${fmt(d.rampexFee)}</TableCell>
                    <TableCell className="text-right tabular-nums text-red-600">{d.rampexPct}%</TableCell>
                    <TableCell className="text-right tabular-nums text-purple-600">${fmt(d.tagadaKeeps)}</TableCell>
                    <TableCell className="text-right tabular-nums text-purple-600">{d.tagadaPct}%</TableCell>
                  </TableRow>
                ))}
                {/* Totals row */}
                <TableRow className="font-semibold border-t-2">
                  <TableCell>TOTAL</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {data.dailyBreakdown.reduce((s, d) => s + d.txns, 0)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    ${fmt(data.dailyBreakdown.reduce((s, d) => s + d.volume, 0))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    ${fmt(data.dailyBreakdown.reduce((s, d) => s + d.poolRecv, 0))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-red-600">
                    ${fmt(data.dailyBreakdown.reduce((s, d) => s + d.rampexFee, 0))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-red-600">
                    {s?.rampexFeePct}%
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-purple-600">
                    ${fmt(data.dailyBreakdown.reduce((s, d) => s + d.tagadaKeeps, 0))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-purple-600">
                    {s && s.totalCustomerPaid > 0
                      ? ((data.dailyBreakdown.reduce((sum, d) => sum + d.tagadaKeeps, 0) / s.totalCustomerPaid) * 100).toFixed(2)
                      : 0}%
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Fee Structure */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Fee Structure</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : (
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-1">
                <p className="text-muted-foreground text-xs">Rampex + Provider (variable)</p>
                <p className="text-lg font-semibold text-red-500">{s?.rampexFeePct}%</p>
                <p className="text-muted-foreground text-xs">Deducted before pool receives funds</p>
              </div>
              <div className="space-y-1">
                <p className="text-muted-foreground text-xs">Merchant fee (PrimePeptide)</p>
                <p className="text-lg font-semibold">7.5%</p>
                <p className="text-muted-foreground text-xs">Of customer-facing amount</p>
              </div>
              <div className="space-y-1">
                <p className="text-muted-foreground text-xs">Default merchant fee</p>
                <p className="text-lg font-semibold">8.5%</p>
                <p className="text-muted-foreground text-xs">Of customer-facing amount</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payout Batches */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Payout Batches</CardTitle>
          <CardDescription>History of payout batches (manual + automated)</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : !data?.batches.length ? (
            <p className="text-muted-foreground py-8 text-center text-sm">No payout batches yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Batch ID</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead className="text-right">Txns</TableHead>
                  <TableHead className="text-right">Customer</TableHead>
                  <TableHead className="text-right">Pool</TableHead>
                  <TableHead className="text-right">Net to Merchant</TableHead>
                  <TableHead className="text-right">Fee %</TableHead>
                  <TableHead>Tx Hash</TableHead>
                  <TableHead>Paid At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.batches.map(b => (
                  <TableRow key={b.batchId}>
                    <TableCell className="font-mono text-xs">{b.batchId.slice(0, 20)}…</TableCell>
                    <TableCell className="text-sm">{b.accountName}</TableCell>
                    <TableCell className="text-right tabular-nums">{b.txnCount}</TableCell>
                    <TableCell className="text-right tabular-nums">${fmt(b.totalCustomer)}</TableCell>
                    <TableCell className="text-right tabular-nums">${fmt(b.totalPool)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">${fmt(b.totalNet)}</TableCell>
                    <TableCell className="text-right tabular-nums">{b.feePct}%</TableCell>
                    <TableCell>
                      {b.txHash ? (
                        <a
                          href={`https://polygonscan.com/tx/${b.txHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 font-mono text-xs text-blue-500 hover:underline"
                        >
                          {shortenHash(b.txHash)}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <Badge variant="outline" className="text-xs">Manual</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {b.paidAt ? new Date(b.paidAt).toLocaleString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      }) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Recent Transactions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Crypto Transactions</CardTitle>
          <CardDescription>Last 50 crypto_sale transactions (newest first)</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Payment ID</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead className="text-right">Customer</TableHead>
                    <TableHead className="text-right">On-Chain</TableHead>
                    <TableHead className="text-right">Net Payout</TableHead>
                    <TableHead className="text-right">Fee</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.recentTransactions.map(t => (
                    <TableRow key={t.id}>
                      <TableCell className="font-mono text-xs">{t.paymentId?.slice(0, 18)}</TableCell>
                      <TableCell className="text-sm max-w-[160px] truncate">{t.accountName}</TableCell>
                      <TableCell className="text-right tabular-nums">${fmt(t.amount)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {t.onChainAmount ? `$${fmt(t.onChainAmount)}` : '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">
                        ${fmt(t.netPayout)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {t.feePct}%
                      </TableCell>
                      <TableCell className="text-center">
                        {t.paidOut ? (
                          <Badge variant="default" className="text-xs">Paid</Badge>
                        ) : t.verified ? (
                          <Badge variant="secondary" className="text-xs">Pending</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">Unverified</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {t.createdAt ? new Date(t.createdAt).toLocaleString('en-US', {
                          month: 'short', day: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        }) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
